/**
 * Client state — the cart, the session, and toasts.
 *
 * Zustand rather than Context, because the cart badge in the header re-renders on every
 * cart change and a Context provider at the root re-renders the entire tree with it.
 * The store subscribes only the components that read it.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { AuthUser } from '@serendib/shared';
import { api, readToken, writeToken, setUnauthorizedHandler } from './api.js';

/* ========================================================================== */
/* Cart                                                                       */
/* ========================================================================== */

export interface CartLine {
  productId: number;
  quantity: number;
  /**
   * A DISPLAY snapshot, taken when the item was added — never the price the order is
   * billed at. The server re-reads every price at checkout (see `services/orders.ts`),
   * so this is only here to render the cart without a round trip. The cart page
   * re-quotes on load and shows the current figure, which is what stops a week-old
   * cart quietly displaying last month's price.
   */
  name: string;
  slug: string;
  unitPrice: string;
  imageUrl: string | null;
}

interface CartState {
  lines: CartLine[];
  add: (line: CartLine) => void;
  setQuantity: (productId: number, quantity: number) => void;
  remove: (productId: number) => void;
  clear: () => void;
  count: () => number;
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],

      add: (line) =>
        set((state) => {
          const existing = state.lines.find((l) => l.productId === line.productId);
          if (existing) {
            // Adding something already in the cart increases the quantity rather than
            // creating a second row. Two rows for one product reserve against the same
            // stock twice and print a confusing order.
            return {
              lines: state.lines.map((l) =>
                l.productId === line.productId
                  ? { ...l, quantity: Math.min(999, l.quantity + line.quantity) }
                  : l,
              ),
            };
          }
          return { lines: [...state.lines, { ...line, quantity: Math.max(1, line.quantity) }] };
        }),

      setQuantity: (productId, quantity) =>
        set((state) => ({
          // Setting a quantity of zero removes the line. Otherwise the cart shows a
          // row that contributes nothing and the customer has to find a second control.
          lines:
            quantity <= 0
              ? state.lines.filter((l) => l.productId !== productId)
              : state.lines.map((l) => (l.productId === productId ? { ...l, quantity: Math.min(999, quantity) } : l)),
        })),

      remove: (productId) => set((state) => ({ lines: state.lines.filter((l) => l.productId !== productId) })),
      clear: () => set({ lines: [] }),
      count: () => get().lines.reduce((n, l) => n + l.quantity, 0),
    }),
    {
      name: 'serendib.cart',
      version: 1,
      /**
       * `createJSONStorage` returns undefined when storage throws, and Zustand then
       * skips persistence rather than crashing. That is the behaviour wanted in a
       * private window: the cart works for the session and simply does not survive a
       * reload.
       */
      storage: createJSONStorage(() => {
        try {
          // Touch it — some browsers only throw on ACCESS, not on the property read.
          localStorage.getItem('serendib.probe');
          return localStorage;
        } catch {
          return {
            getItem: () => null,
            setItem: () => undefined,
            removeItem: () => undefined,
          };
        }
      }),
    },
  ),
);

/* ========================================================================== */
/* Counter configuration                                                      */
/* ========================================================================== */

/**
 * The configurator's current selection.
 *
 * It lives in a store rather than in the configurator component because two sections
 * share it: the `configurator` panel writes it, and the `hero` above reads it to turn
 * the 3D model — that is what "drives the model above" means in the design. Lifting it
 * to a common React ancestor would mean the whole page re-rendering on every toggle,
 * and the two sections are siblings in a list rendered from the database, so there is
 * no natural ancestor to lift to.
 *
 * Deliberately NOT persisted. A configuration is a thought someone is having now, and
 * restoring last week's half-finished counter on a fresh visit is confusing rather than
 * helpful.
 */
interface CounterState {
  /** `optionKey -> choiceValue`. Seeded from the section payload on first render. */
  choices: Record<string, string>;
  lanes: number;
  setChoice: (key: string, value: string) => void;
  setLanes: (lanes: number) => void;
  seed: (choices: Record<string, string>) => void;
}

export const useCounter = create<CounterState>()((set, get) => ({
  choices: {},
  lanes: 2,
  setChoice: (key, value) => set((s) => ({ choices: { ...s.choices, [key]: value } })),
  setLanes: (lanes) => set({ lanes: Math.max(1, Math.min(24, lanes)) }),
  seed: (choices) => {
    // Only fills what has not been chosen yet, so a re-render cannot wipe a selection.
    const current = get().choices;
    const next = { ...choices, ...current };
    set({ choices: next });
  },
}));

/* ========================================================================== */
/* Session                                                                    */
/* ========================================================================== */

interface AuthState {
  user: AuthUser | null;
  /** True until the first `/auth/me` settles. Screens must wait for it. */
  isLoading: boolean;
  signIn: (username: string, password: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  restore: () => Promise<void>;
  clearLocal: () => void;
}

export const useAuth = create<AuthState>()((set) => ({
  user: null,
  // Starts true when a token exists, so a protected route shows a loading state rather
  // than bouncing to the sign-in screen for the moment before `/auth/me` answers. That
  // flash is the difference between "signed in" and "signed out, apparently at random".
  isLoading: Boolean(readToken()),

  signIn: async (username, password) => {
    const result = await api.post<{ token: string; user: AuthUser }>(
      '/auth/login',
      { username, password },
      { auth: false },
    );
    writeToken(result.token);
    set({ user: result.user, isLoading: false });
    return result.user;
  },

  signOut: async () => {
    // Best-effort: the endpoint only records the event, and a network failure must not
    // leave someone unable to sign out.
    await api.post('/auth/logout').catch(() => undefined);
    writeToken(null);
    set({ user: null, isLoading: false });
  },

  clearLocal: () => {
    writeToken(null);
    set({ user: null, isLoading: false });
  },

  restore: async () => {
    if (!readToken()) {
      set({ user: null, isLoading: false });
      return;
    }
    try {
      set({ user: await api.get<AuthUser>('/auth/me'), isLoading: false });
    } catch {
      writeToken(null);
      set({ user: null, isLoading: false });
    }
  },
}));

// A 401 anywhere clears the session, so an expired token cannot leave the back office
// rendering shells that each fail on their own.
setUnauthorizedHandler(() => useAuth.getState().clearLocal());

/* ========================================================================== */
/* Toasts                                                                     */
/* ========================================================================== */

export interface Toast {
  id: number;
  kind: 'success' | 'error' | 'info';
  message: string;
}

interface ToastState {
  toasts: Toast[];
  push: (kind: Toast['kind'], message: string) => void;
  dismiss: (id: number) => void;
}

let toastId = 0;

export const useToasts = create<ToastState>()((set) => ({
  toasts: [],
  push: (kind, message) => {
    const id = ++toastId;
    set((state) => ({ toasts: [...state.toasts, { id, kind, message }] }));
    // Errors stay twice as long. A failure someone needs to read and act on should not
    // vanish in the time it takes to look back at the screen.
    window.setTimeout(() => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })), kind === 'error' ? 8000 : 4000);
  },
  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}));

/** Shorthand usable outside a component — a mutation's `onError`, for instance. */
export const toast = {
  success: (message: string) => useToasts.getState().push('success', message),
  error: (message: string) => useToasts.getState().push('error', message),
  info: (message: string) => useToasts.getState().push('info', message),
};
